import { useState, useRef, useEffect } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { FaHome, FaSignOutAlt, FaBriefcase, FaUsers, FaUserFriends, FaCog, FaUserCircle } from "react-icons/fa";
import { getAuth, signOut } from "firebase/auth";
import logo from "../assets/logo.png";

const menuItems = [
  { to: "/admin", icon: FaHome, label: "Dashboard" },
  { to: "/admin/negocios", icon: FaBriefcase, label: "Negocios" },
  { to: "/admin/agentes-grupos", icon: FaUserFriends, label: "Agentes y Grupos" },
  { to: "/admin/usuarios", icon: FaUsers, label: "Usuarios" },
  { to: "/admin/ajustes", icon: FaCog, label: "Ajustes" },
];

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-5 shadow-sm bg-white border-b border-gray-200">
        {/* Logo */}
        <Link to="/admin" className="flex items-center gap-2">
          <img src={logo} alt="Logo" className="w-9 h-9 object-contain" />
        </Link>

        {/* Profile circle + dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-[#701730] to-[#a3243e] text-white flex items-center justify-center hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#701730]/40"
            title="Menú"
          >
            <FaUserCircle size={22} />
          </button>

          {/* Dropdown */}
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-fadeIn">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                      isActive
                        ? "bg-[#701730]/10 text-[#701730] font-semibold"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className={`text-lg ${isActive ? "text-[#701730]" : "text-gray-500"}`} />
                    {item.label}
                  </Link>
                );
              })}

              <div className="border-t border-gray-100 my-1" />

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
              >
                <FaSignOutAlt className="text-lg" />
                Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-14">
        <div className="p-6 bg-gray-50 min-h-[calc(100vh-3.5rem)]">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
